/**
 * aides-tertiaire.ts
 * ─────────────────────────────────────────────────────────────────
 * Configuration centralisée des pages "stubs" /tertiaire/aides/[slug].
 *
 * Audience : propriétaires / exploitants d'immeubles tertiaires
 * ≥ 1 000 m² (bureaux, commerces, hôtels, ERP, datacenters).
 */

import type { AideStub } from './aides-particuliers';

export const AIDES_TERTIAIRE: AideStub[] = [
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'cee-bat',
    iconName: 'sparkles',
    eyebrow: "Certificats d'Économies d'Énergie, secteur BAT",
    title: 'CEE BAT pour patrimoine tertiaire',
    shortLabel: 'CEE BAT',
    lead: "Des aides pour vos immeubles : chauffage, climatisation, isolation, éclairage. Le principal levier pour financer votre trajectoire de réduction réglementaire.",
    amount: 'Selon fiche & volume',
    amountLabel: "Versement après travaux",
    keyPoints: [
      {
        iconName: 'sparkles',
        label: 'Levier-clé de votre plan',
        description: "Pour chaque opération inscrite à votre plan de réduction, vérifier l'existence d'une fiche applicable avant d'arbitrer le calendrier de réalisation.",
      },
      {
        iconName: 'file-text',
        label: 'Trois familles',
        description: "Thermique (pompe à chaleur, pilotage, eau chaude solaire, ventilation double flux), enveloppe (toiture-terrasse, murs, planchers bas), équipements (LED, variateurs, robinets thermostatiques).",
      },
      {
        iconName: 'file-signature',
        label: 'Engagement avant le devis',
        description: "L'aide doit être demandée avant de signer le devis. Sinon, dossier non éligible.",
      },
      {
        iconName: 'wallet',
        label: 'Cumulable',
        description: "Cumul possible avec le bonus chauffage, l'avantage fiscal pour équipements de chaleur renouvelable et les prêts à taux préférentiel.",
      },
    ],
    conditions: [
      "Propriétaire ou exploitant d'un local tertiaire ≥ 1 000 m² (cumulé)",
      "Travaux éligibles à une fiche BAT-* applicable",
      "Demande d'aide avant signature du devis",
      "Conservation des justificatifs 9 ans : devis, factures, pièces de chantier",
      "Réalisation par un professionnel disposant des qualifications requises",
    ],
    cumulableWith: ['Bonus chauffage tertiaire', 'Avantage fiscal', 'Prêts à taux préférentiel', 'Aides régionales'],
    faq: [
      {
        question: "Quelles fiches sont applicables à mon immeuble ?",
        answer:
          "Sur un immeuble tertiaire courant, les fiches les plus fréquentes : pompe à chaleur (BAT-TH-104), pilotage intelligent (BAT-TH-116), ventilation double flux (BAT-TH-125), calorifugeage (BAT-TH-146), eau chaude solaire collective (BAT-TH-152), isolation toiture (BAT-EN-101) et relamping LED (BAT-EQ-127). On identifie celles qui s'appliquent à votre patrimoine.",
      },
      {
        question: "L'aide est-elle nécessaire pour mes obligations réglementaires ?",
        answer:
          "Non. Vos obligations vous imposent une trajectoire de réduction des consommations, sans imposer de mécanisme de financement. En revanche, les aides sont l'outil de financement principal des opérations de votre plan — ne pas les mobiliser revient à payer plein tarif un investissement déjà nécessaire.",
      },
      {
        question: "Faut-il un diagnostic préalable ?",
        answer:
          "Le diagnostic n'est pas exigé pour mobiliser l'aide sur une opération standardisée isolée. Il devient pertinent pour préparer votre déclaration (calage de l'année de référence) et hiérarchiser votre plan d'actions sur 6 à 10 ans.",
      },
    ],
    ctaLabel: 'Estimer mes primes CEE BAT',
    ctaHref: '/tertiaire/diagnostic-cee',
    secondaryCta: { label: 'Voir le catalogue fiches bâtiment tertiaire (BAT)', href: '/cee/fiches?secteur=BAT' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'coup-de-pouce-cvc',
    iconName: 'flame',
    eyebrow: 'Bonification chauffage, ventilation et climatisation,',
    title: 'Coup de pouce CVC tertiaire',
    shortLabel: 'Coup de pouce CVC',
    lead: "Un bonus qui s'ajoute à votre aide si vous remplacez une chaudière par une pompe à chaleur, installez une ventilation double flux ou vous raccordez à un réseau de chaleur.",
    amount: 'Bonification CEE',
    amountLabel: 'Aide majorée pour opérations CVC ciblées',
    keyPoints: [
      {
        iconName: 'flame',
        label: 'Opérations ciblées',
        description: "Remplacement chaudière fioul ou gaz par pompe à chaleur ou raccordement réseau de chaleur, ventilation double flux avec récupération.",
      },
      {
        iconName: 'building-2',
        label: 'Spécifique tertiaire',
        description: "Distinct du bonus résidentiel. Applicable aux bâtiments à usage tertiaire (bureaux, commerces, hôtels, restaurants, datacenters).",
      },
      {
        iconName: 'sparkles',
        label: "S'ajoute à l'aide de base",
        description: "Le bonus s'ajoute mécaniquement à l'aide standard — c'est une majoration, pas une aide séparée.",
      },
      {
        iconName: 'file-signature',
        label: 'Partenaire agréé',
        description: "Bonus versé par un partenaire agréé pour ce dispositif.",
      },
    ],
    conditions: [
      "Opérations éligibles définies par le dispositif applicable",
      "Bâtiment à usage tertiaire (bureaux, commerces, hôtellerie, restauration…)",
      "Demande d'aide avant signature du devis",
      "Installateur qualifié pour le poste concerné",
      "Versement via un partenaire agréé",
    ],
    cumulableWith: ['Aide de base', 'Avantage fiscal', 'Prêts à taux préférentiel', 'Aides régionales'],
    faq: [
      {
        question: "Quelle différence entre l'aide de base et le bonus ?",
        answer:
          "L'aide de base est versée pour toute opération éligible à une fiche applicable. Le bonus est une majoration ciblée sur certaines opérations prioritaires (remplacement chaudière fioul, raccordement réseau de chaleur, ventilation performante). Les deux se cumulent automatiquement quand l'opération est doublement éligible.",
      },
      {
        question: "Quelles opérations bénéficient du bonus ?",
        answer:
          "Les opérations éligibles évoluent au fil des arrêtés. Historiquement, le bonus a ciblé : le remplacement de chaudière fioul par pompe à chaleur, le raccordement à un réseau de chaleur, et la ventilation double flux avec récupération. Vérifiez les dispositifs applicables lors de votre demande.",
      },
      {
        question: "Comment trouver un partenaire agréé ?",
        answer:
          "La liste des partenaires agréés est publiée par le ministère de la Transition écologique. Vous pouvez aussi passer par un délégataire qui gère le dépôt et vous décharge des démarches administratives.",
      },
    ],
    ctaLabel: 'Estimer mon Coup de pouce CVC',
    ctaHref: '/tertiaire/diagnostic-cee',
    secondaryCta: { label: 'Demander un RDV stratégique', href: '/contact?source=tertiaire&besoin=coup-de-pouce-cvc' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'suramortissement',
    iconName: 'percent',
    eyebrow: 'Avantage fiscal pour équipements de chaleur renouvelable,',
    title: 'Suramortissement chaleur renouvelable',
    shortLabel: 'Suramortissement',
    lead: "Un avantage fiscal qui vous permet de déduire une partie du coût de vos équipements de production de chaleur renouvelable : pompes à chaleur, biomasse, géothermie.",
    amount: 'Déduction fiscale',
    amountLabel: "Selon nature de l'équipement et durée d'amortissement",
    keyPoints: [
      {
        iconName: 'percent',
        label: 'Avantage fiscal direct',
        description: "Déduction supplémentaire sur votre résultat imposable, en plus de l'amortissement comptable classique.",
      },
      {
        iconName: 'flame',
        label: 'Équipements chaleur ciblés',
        description: "Production de chaleur ou de froid à partir d'énergies renouvelables (biomasse, géothermie, solaire thermique, pompes à chaleur industrielles, récupération de chaleur), stockage thermique, réseaux de chaleur renouvelable.",
      },
      {
        iconName: 'building-2',
        label: 'Toutes les sociétés',
        description: "Ouvert aux sociétés soumises à l'IS et aux entreprises individuelles relevant des bénéfices industriels, commerciaux ou agricoles.",
      },
      {
        iconName: 'sparkles',
        label: 'Cumulable avec aides',
        description: "L'avantage fiscal et l'aide travaux portent sur des assiettes différentes (déduction fiscale vs aide versée) et peuvent se combiner sur un même équipement.",
      },
    ],
    conditions: [
      "Société soumise à l'IS, ou à l'IR dans la catégorie des BIC ou BA",
      "Équipement éligible (production ou réseau de chaleur renouvelable, stockage thermique)",
      "Acquisition dans la fenêtre temporelle prévue par la loi de finances applicable",
      "Inscription à l'actif immobilisé et amortissement selon les règles fiscales",
      "Documentation comptable et fiscale conservée (facture, fiche technique)",
    ],
    cumulableWith: ['Aide travaux', 'Bonus chauffage tertiaire', 'Prêts à taux préférentiel', 'Aides régionales'],
    faq: [
      {
        question: "Quels équipements sont éligibles à l'avantage fiscal ?",
        answer:
          "Les équipements de production de chaleur ou de froid à partir d'énergies renouvelables (biomasse, géothermie, solaire thermique, récupération de chaleur), les équipements de stockage thermique et certains équipements de réseaux de chaleur. Les cas d'usage les plus fréquents sur un patrimoine tertiaire ≥ 1 000 m² : chaufferie biomasse, pompe à chaleur haute température, stockage thermique d'eau glacée.",
      },
      {
        question: "Puis-je cumuler avantage fiscal et aide travaux ?",
        answer:
          "Oui. L'avantage fiscal est une déduction sur votre résultat imposable, l'aide travaux est un revenu. Les deux n'opèrent pas sur la même assiette. Documentez clairement le traitement comptable et fiscal de chaque flux avec votre expert-comptable.",
      },
      {
        question: "Comment activer l'avantage fiscal ?",
        answer:
          "Le dispositif s'applique directement sur votre déclaration fiscale, sans démarche préalable. Parlez-en à votre expert-comptable avant l'investissement, et conservez toutes les pièces justifiant l'éligibilité de l'équipement (facture, fiche technique).",
      },
    ],
    ctaLabel: 'Identifier les équipements éligibles',
    ctaHref: '/tertiaire/diagnostic-cee',
    secondaryCta: { label: 'En parler avec un conseiller', href: '/contact?source=tertiaire&besoin=suramortissement' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'prets-bpifrance',
    iconName: 'banknote',
    eyebrow: 'Bpifrance, dispositif Climat',
    title: 'Prêts Bpifrance climat',
    shortLabel: 'Prêts Bpifrance',
    lead: "Des prêts à conditions avantageuses pour financer vos travaux de performance énergétique. Sans prise de garantie sur vos actifs.",
    amount: 'Variable',
    amountLabel: "Montant, durée et taux selon le prêt et le projet",
    keyPoints: [
      {
        iconName: 'banknote',
        label: 'Sans garantie sur les actifs',
        description: "La plupart des prêts sont accordés sans prise de garantie sur les actifs ni caution personnelle du dirigeant.",
      },
      {
        iconName: 'building-2',
        label: 'Cible PME et ETI',
        description: "Dispositifs accessibles aux PME (jusqu'à 250 salariés) et à certaines ETI, ouverts à de nombreux secteurs : foncières, exploitants tertiaires, hôtellerie-restauration, commerce, services.",
      },
      {
        iconName: 'sparkles',
        label: 'Cumul avec aides et subventions',
        description: "Les prêts peuvent financer le reste à charge après aides et subventions — utile pour préserver la trésorerie sur un programme pluriannuel.",
      },
      {
        iconName: 'file-text',
        label: 'Dossier instruit par Bpifrance',
        description: "Montage d'un dossier de financement classique (business plan, devis, plan de financement, derniers comptes). Décision après analyse.",
      },
    ],
    conditions: [
      "PME (jusqu'à 250 salariés, CA ≤ 50 M€ ou bilan ≤ 43 M€) ; certaines ETI éligibles selon dispositif",
      "Projet à dimension énergie ou environnement éligible au dispositif Climat",
      "Présentation d'un business plan, devis, plan de financement et derniers comptes",
      "Inscription au registre du commerce et situation à jour des obligations fiscales et sociales",
      "Conditions exactes (montant, durée, taux) variables selon le prêt et l'évaluation",
    ],
    cumulableWith: ['Aide travaux', 'Bonus chauffage tertiaire', 'Avantage fiscal', 'Aides régionales'],
    faq: [
      {
        question: "Quels prêts financent la rénovation tertiaire ?",
        answer:
          "Les principaux : le Prêt Vert (cofinancé avec l'ADEME, pour des projets de performance énergétique) et le Prêt Économies d'Énergie (travaux d'efficacité énergétique). La liste à jour figure sur bpifrance.fr.",
      },
      {
        question: "Quel est le délai d'obtention ?",
        answer:
          "Quelques semaines entre le dépôt et la décision, selon la complexité du projet. Compter un délai supplémentaire pour la mise à disposition des fonds. Pour un programme tertiaire pluriannuel, déposez le dossier en amont du calendrier.",
      },
      {
        question: "Puis-je cumuler un prêt avec une aide travaux ?",
        answer:
          "Oui, c'est un cas d'usage standard. Le prêt finance l'investissement initial, l'aide rembourse une partie après travaux. L'aide perçue vient réduire l'encours du prêt ou réintégrer la trésorerie. Dimensionnez bien votre plan de financement.",
      },
    ],
    ctaLabel: 'Vérifier mon éligibilité',
    ctaHref: '/contact?source=tertiaire&besoin=prets-bpifrance',
    secondaryCta: { label: 'Diagnostic CEE en parallèle', href: '/tertiaire/diagnostic-cee' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'optimisation-turpe',
    iconName: 'trending-down',
    eyebrow: "Tarif d'acheminement de l'électricité,",
    title: 'Optimisation TURPE immeubles tertiaires',
    shortLabel: 'Optimisation TURPE',
    lead: "La partie transport de vos factures, souvent un quart à un tiers du total. Bureaux vides le week-end, chauffage saisonnier : votre profil se prête bien à l'optimisation des puissances souscrites.",
    amount: 'Économies récurrentes',
    amountLabel: 'Sur la part acheminement de chaque immeuble',
    keyPoints: [
      {
        iconName: 'gauge',
        label: "Puissances calées sur l'occupation",
        description: "Un immeuble de bureaux n'appelle pas la même puissance à 14 h un mardi qu'un dimanche d'août. On compare souscriptions et appels réels pour supprimer le surdimensionnement.",
      },
      {
        iconName: 'building-2',
        label: 'Tous les compteurs du patrimoine',
        description: "Plateaux, communs, parkings, locaux techniques : chaque point de livraison a ses paramètres propres — l'audit les passe tous en revue.",
      },
      {
        iconName: 'trending-down',
        label: 'Formule tarifaire adaptée',
        description: "Courte, moyenne ou longue utilisation : la version tarifaire optimale dépend du taux d'occupation et de la saisonnalité de chaque bâtiment.",
      },
      {
        iconName: 'wallet',
        label: 'Complément naturel de votre plan',
        description: "L'optimisation des tarifs réduit la facture sans toucher aux consommations ; le plan de réduction réduit les consommations elles-mêmes. Les deux se cumulent.",
      },
    ],
    conditions: [
      'Analyse des 12 derniers mois de factures par immeuble (ou courbes de charge des compteurs communicants)',
      "Applicable quel que soit votre fournisseur d'électricité",
      'Modification des paramètres via le fournisseur (répercutée au gestionnaire de réseau)',
      'Revue des pénalités de dépassement et de la version tarifaire par point de livraison',
    ],
    cumulableWith: ['Comparateur énergie tertiaire', 'Aides travaux tertiaires', 'Plan de réduction des consommations', 'Avantage fiscal'],
    faq: [
      {
        question: "C'est quoi le TURPE sur les factures de mes immeubles ?",
        answer:
          "C'est le péage payé pour l'utilisation du réseau public d'électricité, fixé par la Commission de régulation de l'énergie et collecté par votre fournisseur. Il ne se négocie pas, mais ses paramètres (puissances souscrites, version tarifaire) dépendent de chaque contrat — c'est là que se trouvent les économies.",
      },
      {
        question: "L'optimisation compte-t-elle pour mes obligations ?",
        answer:
          "Non — les obligations réglementaires mesurent des consommations (kWh), pas des euros : optimiser les tarifs ne réduit pas vos obligations de réduction. En revanche, les deux démarches se mènent idéalement ensemble : l'audit des factures alimente à la fois le recalage des tarifs (gain immédiat) et le plan d'actions de réduction des consommations (gain durable).",
      },
      {
        question: "Quelle économie espérer sur un immeuble de bureaux ?",
        answer:
          "Tout dépend de l'écart entre les puissances souscrites et les appels réels : un immeuble bien calibré n'a rien à gagner, un immeuble surdimensionné (cas fréquent après un changement d'occupant) peut réduire sensiblement sa part transport. L'audit gratuit le chiffre avant toute décision.",
      },
    ],
    ctaLabel: 'Demander un audit acheminement',
    ctaHref: '/tertiaire/comparateur-energie',
    secondaryCta: { label: 'En parler avec un référent', href: '/contact?source=tertiaire&besoin=optimisation-turpe' },
  },
];

export function getAideTertiaireStub(slug: string): AideStub | undefined {
  return AIDES_TERTIAIRE.find((a) => a.slug === slug);
}
